import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ServerDataSource } from "angular2-smart-table";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { Book, BookData } from "../interfaces/book";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { unset, omit } from 'lodash';

@Injectable()
export class BookService extends BookData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true&populate[authors]=*`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'books';
  }

  listAll(): Observable<Book[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0');

    // Use 'with' for population
    // params = params.append('populate', '*');

    return this.api.get(this.url, { params }).pipe(map(res => {
      // Strapi v5: res is likely an array or object with a 'data' property
      return res.data || res;
    }));
  }

  list(pageNumber: number, pageSize: number, query: string = '' ): Observable<Book[]> {
    let params: HttpParams = new HttpParams()
      // .append('populate', '*');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // If you have filters, sorts, etc. in query, convert them to v5 format
    // Example: ?where[title][$contains]=foo&order=createdAt:desc
    if (query) {
      // You may need to parse and append query params here
      // For now, assume query is already in v5 format
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => {
      return res.data || res;
    }));
  }


  rawQry(pageNumber: number, pageSize: number, params: HttpParams) {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting
    // params = params.append('populate', '*');
    // params = params.append('where', ...);
    // params = params.append('order', ...);

    return this.api.get(this.url, { params }).pipe(map(res => {
      return res.data || res;
    }));
  }


  get(id: any): Observable<Book> {
    return this.api.get(`${this.url}/${id}`)
      .pipe(map(res => res.data || res));
  }


  create(book: Book): Observable<Book> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    return this.api.post(this.url, {data: book}, { params })
      .pipe(map(res => {
        return res.data;
      }));
  }


  update(book: Book): Observable<Book> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    const changedBook = omit(book, ['id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']);
    return this.api.put(`${this.url}/${book.documentId}`, {data: changedBook}, { params })
      .pipe(map(res => {
        return res.data;
      }));
  }


  archive(book: Book, date: Date, user: User): Observable<Book> {
    return this.api.put(`${this.url}/${book.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }
  
  
  unarchive(book: Book): Observable<Book> {
    return this.api.put(`${this.url}/${book.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }


  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }
}
